import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { getSubscriptionInvoices } from '../services/subscriptionService';
import './InvoiceList.css';

function InvoiceList() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [subscription, setSubscription] = useState(null);
  
  const { id: subscriptionId } = useParams();
  const navigate = useNavigate();
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const data = await getSubscriptionInvoices(subscriptionId);
        setInvoices(data.invoices || []);
        setSubscription(data.subscription || null);
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching invoices:', error);
        setError(error.detail || 'Failed to load invoice history');
        setLoading(false);
      }
    };
    
    fetchData();
  }, [subscriptionId]);
  
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };
  
  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return 'N/A';
    
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount / 100);
  };
  
  const formatInvoiceStatus = (status) => {
    switch (status) {
      case 'paid':
        return <span className="status-badge status-paid">Paid</span>;
      case 'open':
        return <span className="status-badge status-open">Unpaid</span>;
      case 'void':
        return <span className="status-badge status-void">Void</span>;
      case 'draft':
        return <span className="status-badge status-draft">Draft</span>;
      case 'uncollectible':
        return <span className="status-badge status-uncollectible">Uncollectible</span>;
      default:
        return <span className="status-badge">{status}</span>;
    }
  };
  
  if (loading) {
    return <div className="loading-container">Loading invoice history...</div>;
  }
  
  if (error) {
    return (
      <div className="error-container">
        <p className="error-message">{error}</p>
        <button className="retry-button" onClick={() => navigate('/profile')}>
          Back to Profile
        </button>
      </div>
    );
  }
  
  return (
    <div className="invoice-list-page">
      <div className="container">
        <div className="page-header">
          <div className="header-content">
            <h1>Invoice History</h1>
            {subscription && (
              <p className="subscription-info">
                Subscription: <strong>{subscription.package_name}</strong>
              </p>
            )}
          </div>
          <Link to="/profile" className="btn btn-outline back-button">
            Back to Profile
          </Link>
        </div>
        
        {invoices.length === 0 ? (
          <div className="no-invoices">
            <p>No invoices found for this subscription.</p>
          </div>
        ) : (
          <div className="invoice-table-container">
            <table className="invoice-table">
              <thead>
                <tr>
                  <th>Invoice #</th>
                  <th>Date</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((invoice) => (
                  <tr key={invoice.id}>
                    <td>
                      <span className="invoice-number">{invoice.number || invoice.id}</span>
                    </td>
                    <td>{formatDate(invoice.created)}</td>
                    <td>{formatCurrency(invoice.amount_paid || invoice.amount_due)}</td>
                    <td>{formatInvoiceStatus(invoice.status)}</td>
                    <td>
                      <div className="invoice-actions">
                        {invoice.invoice_pdf && (
                          <a 
                            href={invoice.invoice_pdf} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="btn btn-sm btn-outline"
                          >
                            View PDF
                          </a>
                        )}
                        {invoice.hosted_invoice_url && (
                          <a 
                            href={invoice.hosted_invoice_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="btn btn-sm btn-outline"
                          >
                            View Online
                          </a>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default InvoiceList; 